import {Injectable, inject} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from 'src/environments/environment';
import {SecurityUtils} from '../auth/security.utils';
import {map} from 'rxjs/operators';
import {ActivatedRoute} from '@angular/router';
import {AuthenticationService} from '../auth/authentication.service';
import {ApplicationTechnology} from 'src/app/services/technologies/application-technology.model';
import {Category} from 'src/app/services/categories/application-category.model';
import {
	ApplicationCostFilter,
	ApplicationCreation,
	ApplicationGeneric,
	ApplicationInstance, ApplicationState, ApplicationStatus, DateFilter,
	Health, HostingType
} from 'src/app/services/applications/new-application.model';
import {ApplicationLifeCycle} from 'src/app/services/applications/application-life-cycle.model';
import {AppCount} from 'src/app/services/applications/autodiscover.model';
import {ContractTypeCost, Cost, Currency, Usage} from 'src/app/services/contracts/application-contract.model';
import {QueryRangeType} from 'src/app/services/dashboards/tenant-finance.service';
import {Rating} from 'src/app/services/applications/application-usage.model';
import {Flow, ProtocolType} from "../flows/flow.service";
import {CustomFieldFilterForm} from "../custom-fields/custom-field.model";

@Injectable({
	providedIn: 'root',
})
export class TenantService {
	protected httpClient = inject(HttpClient);
	protected activatedRoute = inject(ActivatedRoute);
	protected authenticationService = inject(AuthenticationService);

	getApplicationCount(tenantId: string, organizationId?: string): Observable<AppCount> {
		return this.httpClient.get<AppCount>(`${environment.engineURL}/tenants/${tenantId}/app-count`, {
			headers: SecurityUtils.getAuthHttpHeaders(),
			params: !organizationId ? new HttpParams() : new HttpParams().append('organizationId', organizationId)
		});
	}

	createTenant(createTenant: CreateTenant): Observable<string> {
		return this.httpClient.post<string>(environment.engineURL + '/tenants', JSON.stringify(createTenant), {
			headers: SecurityUtils.getAuthHttpHeaders(),
		})
	}

	getTenants(): Observable<TenantOverview[]> {
		// TODO @TAN rework
		return this.httpClient.get<{tenants:{configuration: TenantConfiguration, plan: TenantPlan}[]}>(environment.engineURL + '/tenants', {headers: SecurityUtils.getAuthHttpHeaders()})
			.pipe(map(tenantWrapper => {
				return tenantWrapper.tenants.map(tenant => ({
					configuration: {
						...tenant.configuration,
						permissions: tenant.configuration.permissions || []
					},
					plan: this.buildPlanStatus(tenant.plan)
				}));
			}));
	}

	inviteUsers(tenantId: string, invitationsEmails: InvitationEmail[]): Observable<boolean> {
		// TODO @TAN move to account service
		return this.httpClient.post(environment.engineURL + '/tenants/' + tenantId + '/users', JSON.stringify({users: invitationsEmails}), {headers: SecurityUtils.getAuthHttpHeaders()})
			.pipe(map(() => true));
	}

	deleteTenant(tenantId: string): Observable<void> {
		return this.httpClient.delete<void>(environment.engineURL + '/tenants/' + tenantId,
			{headers: SecurityUtils.getAuthHttpHeaders()});
	}

	protected buildPlanStatus(plan: TenantPlan): PlanStatus {
		switch (plan.planStatus) {
			case 'trialing': return new TrialPlan(new Date(plan.endAt));
			case 'active': return new ActivePlan();
			case 'incomplete': return new InactivePlan('Plan has been cancelled.');
			case 'incomplete_expire': return new InactivePlan('Plan has been cancelled.');
			case 'past_due': return new InactivePlan('Plan has been cancelled.');
			case 'canceled': return new InactivePlan('Plan has been cancelled.');
			case 'unpaid': return new InactivePlan('Something wrong happened while proceeding payment.');
			case 'expired': return new InactivePlan('Subscription has ended.', true);
			default : return new TrialPlan(new Date(plan.endAt));
		}
	}

	getAllApplicationCategoryByTenantId(tenantId: string, teams?: string[]): Observable<ApplicationCategory[]> {
		const params: HttpParams = new HttpParams();
		const body = {
			teams: teams
		}
		return this.httpClient.post<ApplicationCategory[]>(`${environment.engineURL}/tenants/${tenantId}/category-apps`, body, {
			headers: SecurityUtils.getAuthHttpHeaders(),
			params: params
		});
	}

	getAllApplicationComplexityByTenantId(tenantId: string, teams?: string[], limit?: number, range?: QueryRangeType): Observable<ApplicationComplexity[]> {
		let params: HttpParams = new HttpParams();
		const body = {
			teams: teams
		}
		if (limit) params = params.append('limit', limit);
		if (range) params = params.append('range', range);
		return this.httpClient.post<ApplicationComplexity[]>(`${environment.engineURL}/tenants/${tenantId}/complexity-apps`, body, {
			headers: SecurityUtils.getAuthHttpHeaders(),
			params: params
		});
	}

	getAllApplicationDocumentationByTenantId(tenantId: string, teams?: string[], limit?: number, range?: QueryRangeType): Observable<ApplicationDocumentation[]> {
		let params: HttpParams = new HttpParams();
		const body = {
			teams: teams
		}
		if (limit) params = params.append('limit', limit);
		if (range) params = params.append('range', range);
		return this.httpClient.post<ApplicationDocumentation[]>(`${environment.engineURL}/tenants/${tenantId}/documentation-apps`, body, {
			headers: SecurityUtils.getAuthHttpHeaders(),
			params: params
		});
	}

	getAllApplicationObsolescenceByTenantId(tenantId: string, teams?: string[], limit?: number, range?: QueryRangeType): Observable<ApplicationObsolescence[]> {
		let params: HttpParams = new HttpParams();
		const body = {
			teams: teams
		}
		if (limit) params = params.append('limit', limit);
		if (range) params = params.append('range', range);
		return this.httpClient.post<ApplicationObsolescence[]>(`${environment.engineURL}/tenants/${tenantId}/obsolescence-apps`, body, {
			headers: SecurityUtils.getAuthHttpHeaders(),
			params: params
		});
	}

	getTenantOverlapStatistic(tenantId: string, organizationId?: string, range?: QueryRangeType): Observable<TenantOverlapStatistic> {
		let params: HttpParams = new HttpParams();
		if (organizationId) params = params.append("organizationId", organizationId);
		if (range) params = params.append('range', range);
		return this.httpClient.get<TenantOverlapStatistic>(`${environment.engineURL}/tenants/${tenantId}/overlap-statistic`, {
			headers: SecurityUtils.getAuthHttpHeaders(),
			params: params
		});
	}

	getTenantOperationStatistic(tenantId: string, teams?: string[], costRange?: QueryRangeType, elasticRange?: QueryRangeType): Observable<TenantOperationStatistic> {
		let params: HttpParams = new HttpParams();
		if (costRange) params = params.append('costRange', costRange);
		if (elasticRange) params = params.append('elasticRange', elasticRange);
		const body = {
			teams: teams
		}
		return this.httpClient.post<TenantOperationStatistic>(`${environment.engineURL}/tenants/${tenantId}/operation-statistic`, body, {
			headers: SecurityUtils.getAuthHttpHeaders(),
			params: params
		});
	}

	getTenantFinanceStatistic(tenantId: string, teams?: string[], costRange?: QueryRangeType, elasticRange?: QueryRangeType): Observable<TenantFinanceStatistic> {
		let params: HttpParams = new HttpParams();
		const body = {
			teams: teams
		}
		if (costRange) params = params.append('costRange', costRange);
		if (elasticRange) params = params.append('elasticRange', elasticRange);
		return this.httpClient.post<TenantFinanceStatistic>(`${environment.engineURL}/tenants/${tenantId}/finance-statistic`, body, {
			headers: SecurityUtils.getAuthHttpHeaders(),
			params: params
		});
	}

	getTenantUsageStatistic(tenantId: string, teams?: string[], range?: QueryRangeType): Observable<TenantUsageStatistic> {
		let params: HttpParams = new HttpParams();
		const body = {
			teams: teams
		}
		if (range) params = params.append('range', range);
		return this.httpClient.post<TenantUsageStatistic>(`${environment.engineURL}/tenants/${tenantId}/usage-statistic`, body, {
			headers: SecurityUtils.getAuthHttpHeaders(),
			params: params
		});
	}

	getTenantArchitectureStatistic(tenantId: string, teams?: string[]): Observable<TenantArchitectureStatistic> {
		const params: HttpParams = new HttpParams();
		const body = {
			teams: teams
		}
		return this.httpClient.post<TenantArchitectureStatistic>(`${environment.engineURL}/tenants/${tenantId}/architecture-statistic`, body, {
			headers: SecurityUtils.getAuthHttpHeaders(),
			params: params
		});
	}

	getTenantTechnologyStatistic(tenantId: string, teams?: string[]): Observable<TenantTechnologyStatistic> {
		const params: HttpParams = new HttpParams();
		const body = {
			teams: teams
		}
		return this.httpClient.post<TenantTechnologyStatistic>(`${environment.engineURL}/tenants/${tenantId}/technology-statistic`, body, {
			headers: SecurityUtils.getAuthHttpHeaders(),
			params: params
		});
	}

	getTenantDocumentStatistic(tenantId: string, teams?: string[]): Observable<TenantDocumentStatistic> {
		const params: HttpParams = new HttpParams();
		const body = {
			teams: teams
		}
		return this.httpClient.post<TenantDocumentStatistic>(`${environment.engineURL}/tenants/${tenantId}/document-statistic`, body, {
			headers: SecurityUtils.getAuthHttpHeaders(),
			params: params
		});
	}

	getAllApplicationTechnologyByTenantId(tenantId: string, teams?: string[]): Observable<ApplicationTechnology[]> {
		const params: HttpParams = new HttpParams();
		const body = {
			teams: teams
		}
		return this.httpClient.post<ApplicationTechnology[]>(`${environment.engineURL}/tenants/${tenantId}/technology-apps`, body, {
			headers: SecurityUtils.getAuthHttpHeaders(),
			params: params
		});
	}

	getAllApplicationLifeCycleByTenantId(tenantId: string, teams?: string[]): Observable<ApplicationLifeCycle[]> {
		const params: HttpParams = new HttpParams();
		const body = {
			teams: teams
		}
		return this.httpClient.post<ApplicationLifeCycle[]>(`${environment.engineURL}/tenants/${tenantId}/life-cycles`, body, {
			headers: SecurityUtils.getAuthHttpHeaders(),
			params: params
		});
	}

	getAllDiagramApplicationLifeCycle(tenantId: string, form: DiagramApplicationLifeCycleFilterForm = {}): Observable<DiagramApplicationLifeCycle[]> {
		return this.httpClient.post<DiagramApplicationLifeCycle[]>(`${environment.engineURL}/tenants/${tenantId}/diagrams/life-cycles`, form, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	getAllDiagramApplicationFlowsWithMiddlewares(tenantId: string, form: DiagramApplicationFlowFilterForm = {}): Observable<DiagramApplicationFlow[]> {
		return this.httpClient.post<DiagramApplicationFlow[]>(`${environment.engineURL}/tenants/${tenantId}/diagrams/flows-with-middlewares`, form, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	getAllDiagramApplicationMatrix(tenantId: string, form: DiagramApplicationMatrixFilterForm = {}): Observable<DiagramApplicationMatrix[]> {
		return this.httpClient.post<DiagramApplicationMatrix[]>(`${environment.engineURL}/tenants/${tenantId}/diagrams/matrices`, form, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	getAllDiagramApplicationQuadrant(tenantId: string, form: DiagramApplicationQuadrantFilterForm = {}): Observable<DiagramApplicationQuadrant[]> {
		return this.httpClient.post<DiagramApplicationQuadrant[]>(`${environment.engineURL}/tenants/${tenantId}/diagrams/quadrants`, form, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	getTenantExtensionSettingsWithCredentials(tenantId: string): Observable<TenantExtensionSettingsWithCredentials> {
		return this.httpClient.get<TenantExtensionSettingsWithCredentials>(`${environment.engineURL}/tenants/${tenantId}/rover-settings`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	getTenantExtensionSettingsWithoutCredentials(tenantId: string): Observable<TenantExtensionSettingsWithoutCredentials> {
		return this.httpClient.get<TenantExtensionSettingsWithoutCredentials>(`${environment.engineURL}/tenants/${tenantId}/rover-settings/without-credentials`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	updateTenantExtensionSettings(tenantId: string, form: TenantExtensionSettingsForm): Observable<boolean> {
		return this.httpClient.put<boolean>(`${environment.engineURL}/tenants/${tenantId}/rover-settings`, form,{
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	uploadExtensionLogo(tenantId: string, file: File): Observable<boolean> {
		const formData = new FormData();
		formData.append('file', file);
		return this.httpClient.post<boolean>(`${environment.engineURL}/tenants/${tenantId}/rover-settings/icon`, formData, {
			headers: SecurityUtils.getRawAuthHttpHeaders()
		});
	}

	deleteExtensionLogo(tenantId: string): Observable<boolean> {
		return this.httpClient.delete<boolean>(`${environment.engineURL}/tenants/${tenantId}/rover-settings/icon`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	updateTenantName(tenantId: string, name: string): Observable<boolean> {
		return this.httpClient.post<void>(`${environment.engineURL}/tenants/${tenantId}/_rename`, {name}, {
			headers: SecurityUtils.getAuthHttpHeaders()
		}).pipe(map(() => true));
	}

	checkTenantExtensionInstalled(tenantId: string): Observable<boolean> {
		return this.httpClient.get<boolean>(`${environment.engineURL}/tenants/${tenantId}/check-extension-installed`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	setOnboardingCompleted(tenantId: string): Observable<boolean> {
		return this.httpClient.put<boolean>(`${environment.engineURL}/tenants/${tenantId}/onboarding-completed`, null, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	addOnboardingApplicationsToTenant(tenantId: string, body: ApplicationCreation[]): Observable<boolean> {
		return this.httpClient.post<boolean>(`${environment.engineURL}/tenants/${tenantId}/onboarding-applications`, body, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}


	isUserIdentityTrackingEnabled(tenantId: string): Observable<boolean> {
		return this.httpClient.get<boolean>(`${environment.engineURL}/tenants/${tenantId}/user-identity-tracking`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	getKabeenAiSettings(tenantId: string): Observable<KabeenAiSettings> {
		return this.httpClient.get<KabeenAiSettings>(`${environment.engineURL}/tenants/${tenantId}/kabeen-intelligence-settings`, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}

	updateKabeenAiSettings(tenantId: string, form: KabeenAiSettingsForm): Observable<boolean> {
		return this.httpClient.put<boolean>(`${environment.engineURL}/tenants/${tenantId}/kabeen-intelligence-settings`, form, {
			headers: SecurityUtils.getAuthHttpHeaders()
		});
	}
}

export interface TenantOverview {
	// TODO @TAN Rework
	configuration: TenantConfiguration;
	plan: PlanStatus;
}

export interface TenantConfiguration {
	id: string;
	name: string;
	roleName?: string;
	permissions?: string[];
	isDefault: boolean;
	publicLogoUrl?: string;
	supportUrl?: string;
	onboardingCompleted: boolean;
	currency: Currency;
}

export interface TenantPlan {
	planStatus: string;
	endAt: string;
}

export interface CreateTenant {
	name: string;
	accountExpectations?: string;
	organizationSector?: string;
	organizationEmployees?: string;
	userType?: string;
	companyType?: string;
	onboardingCompleted: boolean;
}

export interface DiagramApplicationLifeCycle {
	application: ApplicationGeneric;
	lifeCycle: ApplicationLifeCycle|null;
}

export interface DiagramApplicationLifeCycleFilterForm {
	criticality?: CriticalityLevel[];
	organizations?: string[];
	categories?: string[];
	withNoData?: boolean;
	status?: ApplicationStatus[];
	states?: ApplicationState[];
	tag?: string[];
	customFields?: CustomFieldFilterForm[];
	functionalCapacity?: string[];
	responsible?: string[];
	hostingType?: HostingType[];
	vendor?: string[];
	data?: string[];
	deployedDate?: DateFilter;
	authType?: AuthenticationType[];
	authPrimaryFactor?: AuthenticationPrimaryFactor[];
	authSecondaryFactor?: AuthenticationSecondaryFactor[];
	authProtocol?: AuthenticationProtocol[];
	cost?: ApplicationCostFilter;
	noValueFilters?: string[];
}

export interface DiagramApplicationFlow {
	application: ApplicationGeneric;
	category: Category|null;
	flowList: Flow[];
	usage: Usage | null;
	health: Health;
}

export interface DiagramApplicationFlowFilterForm {
	criticality?: CriticalityLevel[];
	organizations?: string[];
	applications?: string[];
	categories?: string[];
	withNoData?: boolean;
	withExternal?: boolean;
	displayMiddlewares?: boolean;
	displayFlowLabels?: boolean;
	flowLabelData?: string;
	displayInfoBubble?: boolean;
	infoBubbleData?: string;
	protocols?: ProtocolType[];
	ports?: string[];
	status?: ApplicationStatus[];
	states?: ApplicationState[];
	tag?: string[];
	customFields?: CustomFieldFilterForm[]
	functionalCapacity?: string[];
	responsible?: string[];
	hostingType?: HostingType[];
	vendor?: string[];
	data?: string[];
	deployedDate?: DateFilter;
	authType?: AuthenticationType[];
	authPrimaryFactor?: AuthenticationPrimaryFactor[];
	authSecondaryFactor?: AuthenticationSecondaryFactor[];
	authProtocol?: AuthenticationProtocol[];
	cost?: ApplicationCostFilter;
	noValueFilters?: string[];
}

export interface DiagramApplicationMatrix {
	application: ApplicationGeneric;
	categoryId: string|null;
	organizationIds: string[];
}

export interface DiagramApplicationMatrixFilterForm {
	criticality?: CriticalityLevel[];
	organizations?: string[];
	categories?: string[];
	status?: ApplicationStatus[];
	states?: ApplicationState[];
	tag?: string[];
	customFields?: CustomFieldFilterForm[];
	functionalCapacity?: string[];
	responsible?: string[];
	hostingType?: HostingType[];
	vendor?: string[];
	data?: string[];
	deployedDate?: DateFilter;
	authType?: AuthenticationType[];
	authPrimaryFactor?: AuthenticationPrimaryFactor[];
	authSecondaryFactor?: AuthenticationSecondaryFactor[];
	authProtocol?: AuthenticationProtocol[];
	cost?: ApplicationCostFilter;
	noValueFilters?: string[];
}

/**
 * Formulaire de filtres propre à la carte des capacités. Reprend les mêmes critères de requête que
 * le matrix, plus `hideEmptyCapacities` — un état d'AFFICHAGE (masquer les capacités sans application)
 * persisté avec le layout. Ce champ n'est PAS un critère de requête : le back ne doit pas l'utiliser
 * pour filtrer les données (le masquage est géré côté front, cf. diagram-capacity.component).
 */
export interface DiagramApplicationCapacityFilterForm extends DiagramApplicationMatrixFilterForm {
	hideEmptyCapacities?: boolean;
}

/**
 * Sur-ensemble des champs de filtres acceptés par le bandeau de filtres partagé (flux + capacités).
 * Permet à la barre de lire `hideEmptyCapacities` sans polluer le formulaire des flux.
 */
export type DiagramFilterBarFilters = DiagramApplicationFlowFilterForm & DiagramApplicationCapacityFilterForm;

export interface DiagramApplicationQuadrant {
	application: ApplicationGeneric;
	usage: number|null;
	cost: number|null;
	health: number|null;
	lcp: number|null;
	obsolescence: number|null;
}

export interface DiagramApplicationQuadrantFilterForm {
	criticality?: CriticalityLevel[];
	organizations?: string[];
	categories?: string[];
	applications?: string[];
	status?: ApplicationStatus[];
	states?: ApplicationState[];
	tag?: string[];
	customFields?: CustomFieldFilterForm[];
	functionalCapacity?: string[];
	responsible?: string[];
	hostingType?: HostingType[];
	vendor?: string[];
	data?: string[];
	deployedDate?: DateFilter;
	authType?: AuthenticationType[];
	authPrimaryFactor?: AuthenticationPrimaryFactor[];
	authSecondaryFactor?: AuthenticationSecondaryFactor[];
	authProtocol?: AuthenticationProtocol[];
	cost?: ApplicationCostFilter;
	noValueFilters?: string[];
}

export interface ApplicationCategory {
	application: ApplicationInstance;
	category: Category|null;
}

export interface ApplicationComplexity {
	application: ApplicationGeneric;
	complexity: Compliance;
	usage: Usage;
}

export interface ApplicationDocumentation {
	application: ApplicationGeneric;
	documentationPercent: number;
	lastDocumentationDate: Date;
}

export interface ApplicationObsolescence {
	application: ApplicationGeneric;
	obsolescence: Compliance;
	usage: Usage;
}

export interface ApplicationCompliance {
	application: ApplicationGeneric;
	complexity: Compliance;
	obsolescence: Compliance;
}

export interface TenantOverlapStatistic {
	nbDuplicates: number;
	potentialEarnings: Cost;
	overlapPercent: number;
}

export interface TenantOperationStatistic {
	totalApplication: number;
	totalCost: Cost;
	usage: Usage;
	serverCount: number;
	/** Previous 30-day window values for the historized metrics — drives the tendency chips. */
	previousPeriod?: {
		usage: Usage;
	};
}

export interface TenantFinanceStatistic {
	costByTypes: ContractTypeCost[];
	usage: Usage;
	activeContractCount: number;
	potentialEarnings: Cost;
	/** Previous 30-day window values for the historized metrics — drives the tendency chips. */
	previousPeriod?: {
		activeContractCount: number;
	};
}

export interface TenantUsageStatistic {
	usage: Usage;
	satisfaction: Rating;
	discoveredCount: AppCount;
	workstationsCount: number;
	/** Previous 30-day window values for the historized metrics — drives the tendency chips. */
	previousPeriod?: {
		usage: Usage;
		satisfaction: Rating;
		workstationsCount: number;
	};
}

export interface TenantArchitectureStatistic {
	totalApplication: number;
	totalFlows: number;
	totalDocument: number;
	totalServer: number;
	averageAge: number;
}

export interface TenantTechnologyStatistic {
	totalApplication: number;
	totalWorkstation: number;
	totalServer: number;
	/** Previous 30-day window values for the historized metrics — drives the tendency chips. */
	previousPeriod?: {
		totalWorkstation: number;
	};
}

export interface TenantDocumentStatistic {
	totalDocument: number;
	averageCompletion: number;
}

export interface TenantExtensionSettingsForm {
	displayExtensionCheck: boolean;
	supportUrlCheck: boolean;
	supportUrl: string|null
	feedbackCheck: boolean;
	feedback: string|null;
	userIdentityTracking: "tracked" | "anonymous" | "forced_anonymous",
	applicationDisplay: string;
	applicationDisplayActivated: boolean;
}

export interface KabeenAiSettings {
	chatEnabled: boolean;
	applicationsEnabled: boolean;
	teamsEnabled: boolean;
	featuresEnabled: boolean;
	defaultProvider: string;
}

export interface KabeenAiSettingsForm {
	chatEnabled: boolean;
	applicationsEnabled: boolean;
	teamsEnabled: boolean;
	featuresEnabled: boolean;
	defaultProvider: string;
}

export enum KabeenAiProvider {
	MISTRAL = 'mistral',
	OPENAI = 'openai',
	ANTHROPIC = 'anthropic'
}

export interface TenantExtensionSettingsWithCredentials {
	token: string,
	satisfactionActivated: boolean,
	satisfactionFrequency: string,
	supportUrlActivated: boolean,
	supportUrl: string|null,
	displayRover: boolean,
	publicLogoUrl: string|null,
	userIdentityTracking: "tracked" | "anonymous" | "forced_anonymous",
	teams: TeamOverview[]
	applicationDisplay: ExtensionApplicationDisplay,
	applicationDisplayActivated: boolean
}

export interface TenantExtensionSettingsWithoutCredentials {
	satisfactionActivated: boolean,
	satisfactionFrequency: string,
	supportUrlActivated: boolean,
	supportUrl: string|null,
	displayRover: boolean,
	publicLogoUrl: string|null,
	userIdentityTracking: "tracked" | "anonymous" | "forced_anonymous",
	teams: TeamOverview[]
	applicationDisplay: ExtensionApplicationDisplay,
	applicationDisplayActivated: boolean
}

export enum ExtensionApplicationDisplay {
	ALL = 'all',
	OWNED = 'owned',
	CUSTOM = 'custom'
}

export interface TeamOverview {
	teamId: string,
	structureId: string,
	path: string,
	name: string
}

export interface Compliance {
	percent: number;
	level: ComplianceLevel;
}

export enum ComplianceLevel {
	A = 'A',
	B = 'B',
	C = 'C',
	D = 'D'
}

export enum CriticalityLevel {
	HIGH = 'HIGH',
	MEDIUM = 'MEDIUM',
	LOW = 'LOW'
}

export enum AuthenticationType {
	LOCAL = 'local',
	SSO = 'sso',
	SSO_AZURE = 'sso_azure',
	SSO_GOOGLE = 'sso_google',
	SSO_KEYCLOAK = 'sso_keycloak',
	SSO_OKTA = 'sso_okta',
	SSO_GITHUB = 'sso_github',
	SSO_SLACK = 'sso_slack',
	OTHER = 'other'
}

export enum AuthenticationPrimaryFactor {
	LOGIN_PASSWORD = 'login_password',
	SMART_CARD = 'smart_card',
	CERTIFICATE = 'certificate',
	FIDO = 'fido',
	PIN = 'pin',
	BIOMETRY = 'biometry'
}

export enum AuthenticationSecondaryFactor {
	BIOMETRY = 'biometry',
	OTP = 'otp',
	SMART_CARD = 'smart_card',
	CERTIFICATE = 'certificate',
	FIDO = 'fido',
	PIN = 'pin',
	APPLICATION = 'application'
}

export enum AuthenticationProtocol {
	KERBEROS = 'kerberos',
	OAUTH = 'oauth',
	SAMLV2 = 'samlv2',
	OPENID_CONNECT = 'openid_connect',
	LDAP = 'ldap',
	OTHER = 'other'
}

export interface PlanStatus {
	// TODO @TAN Rework
	isActivePlan(): boolean;
	asActivePlan(): ActivePlan;

	isTrialPlan(): boolean;
	asTrialPlan(): TrialPlan;

	isInactivePlan(): boolean;
	asInactivePlan(): InactivePlan;
}

export interface InvitationEmail {
	email: string;
	grantAccess: boolean;
	role?: string;
}

export abstract class BasePlanStatus implements PlanStatus {
	// TODO @TAN Rework
	asActivePlan(): ActivePlan {
		throw new Error("Not an active plan.");
	}

	asInactivePlan(): InactivePlan {
		throw new Error("Not an inactive plan.");
	}

	asTrialPlan(): TrialPlan {
		throw new Error("Not a trial plan.");
	}

	isActivePlan(): boolean {
		return false;
	}

	isInactivePlan(): boolean {
		return false;
	}

	isTrialPlan(): boolean {
		return false;
	}

}

export class ActivePlan extends BasePlanStatus {
	isActivePlan(): boolean {
		return true;
	}

	asActivePlan(): ActivePlan {
		return this;
	}
}

export class TrialPlan extends BasePlanStatus {
	constructor(public endAt: Date) {
		super();
	}
	isTrialPlan(): boolean {
		return true;
	}

	asTrialPlan(): TrialPlan {
		return this;
	}
}

export class InactivePlan extends BasePlanStatus {
	/** `subscriptionEnded`: an active plan past its end date (KAB-2399) — every other inactive plan ended a trial. */
	constructor(public reason: string, public subscriptionEnded = false) {
		super();
	}
	isInactivePlan(): boolean {
		return true;
	}

	asInactivePlan(): InactivePlan {
		return this;
	}
}
